import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { Loader } from '../ui';
import { cn } from '../../lib/utils';
import { TOPIC_ICONS } from '../../constants/icons';
import confetti from 'canvas-confetti';
import { MOCKUPS } from '../landing/mockups';
import { COLOR_MAPPINGS } from '../../utils/colorUtils';
import { useCreateTopic } from '../../hooks/queries/useTopics';

interface NewTopicModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  onTopicSelect: (topicId: string) => void;
  isFirstTopic?: boolean;
}

export function NewTopicModal({
  isOpen,
  onClose,
  projectId,
  onTopicSelect,
  isFirstTopic = false,
}: NewTopicModalProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState<string>('todo');
  const [error, setError] = useState<string | null>(null);
  
  // Use React Query mutation hook
  const createTopic = useCreateTopic();
  const isSubmitting = createTopic.isPending;

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setName('');
      setDescription('');
      setType('todo');
      setError(null);
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim() || isSubmitting) return;

    setError(null);

    try {
      const topic = await createTopic.mutateAsync({
        projectId,
        name: name.trim(),
        description: description.trim(),
        topicType: type
      });

      if (topic) {
        // Fire confetti on success
        confetti({
          particleCount: 150,
          spread: 300,
          origin: { y: 0.3 },
          colors: ['#3b82f6', '#60a5fa', '#93c5fd', '#2563eb', '#1d4ed8'],
          startVelocity: 45,
          gravity: 1.2,
          scalar: 1.2,
          ticks: 150,
        });

        onTopicSelect(topic.id);
        onClose();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create topic');
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        role="dialog"
        aria-labelledby="modal-title"
        aria-modal="true"
        className="fixed inset-0 sm:inset-auto sm:left-1/2 sm:top-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      >
        <div className="bg-white w-full sm:w-[95vw] max-w-5xl sm:rounded-xl shadow-xl max-h-[100vh] sm:max-h-[95vh] flex flex-col overflow-hidden">
          {/* Top Bar with title and actions */}
          <div className="px-4 sm:px-6 py-4 flex items-center justify-between bg-gray-50">
            <h2 className="text-xl font-bold font-sora text-gray-800">
              {isFirstTopic ? (
                <>
                  <span className="hidden sm:inline">Create Your First Topic</span>
                  <span className="sm:hidden">First Topic</span>
                </>
              ) : (
                <>
                  <span className="hidden sm:inline">Create New Topic</span>
                  <span className="sm:hidden">New Topic</span>
                </>
              )}
            </h2>
            <div className="flex items-center gap-3">
              {/* Mobile cancel button (X icon) */}
              <button
                type="button"
                onClick={onClose}
                className={cn(
                  'sm:hidden w-9 h-9 flex items-center justify-center bg-gray-100 text-gray-700 rounded-lg',
                  'hover:bg-gray-200 transition-colors',
                  'disabled:opacity-50 disabled:cursor-not-allowed'
                )}
                disabled={isSubmitting}
                aria-label="Cancel"
              >
                <X size={20} />
              </button>

              {/* Desktop cancel button (text) */}
              <button
                type="button"
                onClick={onClose}
                className={cn(
                  'hidden sm:block px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-base',
                  'hover:bg-gray-200 transition-colors',
                  'disabled:opacity-50 disabled:cursor-not-allowed'
                )}
                disabled={isSubmitting}
              >
                Cancel
              </button>

              {/* Create button - simplified on mobile */}
              <button
                type="submit"
                form="new-topic-form"
                className={cn(
                  'px-4 py-2 bg-blue-500 text-white rounded-lg text-sm sm:text-base',
                  'hover:bg-blue-600 transition-colors',
                  'disabled:opacity-50 disabled:cursor-not-allowed',
                  'flex items-center justify-center gap-2 font-medium'
                )}
                disabled={!name.trim() || isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader size="small" />
                    <span className="hidden sm:inline">Creating...</span>
                    <span className="sm:hidden">...</span>
                  </>
                ) : (
                  <>
                    <span>Create</span>
                    <span className="hidden sm:inline"> Topic</span>
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Form */}
          <form
            id="new-topic-form"
            onSubmit={handleSubmit}
            className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-6 relative"
          >
            {/* Topic name and description */}
            <div className="grid sm:grid-cols-10 gap-6">
              <div className="sm:col-span-4">
                <label
                  htmlFor="topic-name"
                  className="block text-base font-bold text-gray-800 mb-2 font-sora"
                >
                  Topic Name
                </label>
                <input
                  id="topic-name"
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  className={cn(
                    'w-full rounded-lg px-3 py-2 shadow-md',
                    'bg-gray-50',
                    'focus:outline-none',
                    'disabled:opacity-50 disabled:cursor-not-allowed',
                    'transition-colors duration-200'
                  )}
                  placeholder="Enter topic name"
                  disabled={isSubmitting}
                  required
                  autoFocus
                />
              </div>

              <div className="sm:col-span-6">
                <label
                  htmlFor="topic-description"
                  className="block text-base font-bold text-gray-800 mb-2 font-sora"
                >
                  Description
                </label>
                <textarea
                  id="topic-description"
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  className={cn(
                    'w-full rounded-lg px-3 py-2 shadow-md',
                    'bg-gray-50',
                    'focus:outline-none',
                    'disabled:opacity-50 disabled:cursor-not-allowed',
                    'transition-colors duration-200',
                    'resize-none'
                  )}
                  placeholder="Enter topic description (optional)"
                  rows={1}
                  disabled={isSubmitting}
                />
              </div>
            </div>

            {/* Topic type selection - Desktop Version */}
            <div className="mt-4 px-1.5 hidden sm:block">
              <div className="grid grid-cols-7 gap-6">
                {Object.entries(TOPIC_ICONS)
                  // Reorder to put todo first
                  .sort(([a], [b]) => (a === 'todo' ? -1 : b === 'todo' ? 1 : 0))
                  .map(([typeKey, { icon: Icon, label, description: typeDesc }]) => {
                    // Map topic types to color utilities
                    const colorMap: Record<string, string> = {
                      todo: 'green',
                      notes: 'purple',
                      ai_chat: 'blue',
                      list: 'orange',
                      moodboard: 'pink',
                      bookmarks: 'yellow',
                      table: 'cyan',
                    };

                    const colorKey = colorMap[typeKey] || 'gray';
                    const iconColorClass = COLOR_MAPPINGS.icon[colorKey];
                    const bgHoverClass = `hover:bg-${colorKey}-50`;
                    const activeBgClass = type === typeKey ? COLOR_MAPPINGS.bg[colorKey] : '';

                    return (
                      <button
                        key={`desktop-${typeKey}`}
                        type="button"
                        onClick={() => setType(typeKey)}
                        className={cn(
                          'px-1.5 py-3 text-center rounded-lg transition-all duration-200 h-full flex flex-col justify-between',
                          type === typeKey ? activeBgClass : `bg-gray-50 ${bgHoverClass}`
                        )}
                      >
                        <div className="flex flex-col items-center gap-1 mb-1">
                          <Icon size={24} className={iconColorClass} />
                          <span className="font-bold text-sm font-sora">{label}</span>
                        </div>
                        <div className="text-xs text-gray-500 h-12 overflow-y-auto">{typeDesc}</div>
                      </button>
                    );
                  })}
              </div>
            </div>

            {/* Topic type selection - Mobile Version */}
            <div className="mt-4 sm:hidden space-y-2.5">
              {Object.entries(TOPIC_ICONS)
                // Reorder to put todo first
                .sort(([a], [b]) => (a === 'todo' ? -1 : b === 'todo' ? 1 : 0))
                .map(([typeKey, { icon: Icon, label, description: typeDesc }]) => {
                  // Map topic types to color utilities
                  const colorMap: Record<string, string> = {
                    todo: 'green',
                    notes: 'purple',
                    ai_chat: 'blue',
                    list: 'orange',
                    moodboard: 'pink',
                    bookmarks: 'yellow',
                    table: 'cyan',
                  };

                  const colorKey = colorMap[typeKey] || 'gray';
                  const iconColorClass = COLOR_MAPPINGS.icon[colorKey];
                  const bgHoverClass = `hover:bg-${colorKey}-50`;
                  const activeBgClass = type === typeKey ? COLOR_MAPPINGS.bg[colorKey] : '';

                  return (
                    <button
                      key={`mobile-${typeKey}`}
                      type="button"
                      onClick={() => setType(typeKey)}
                      className={cn(
                        'w-full p-3 rounded-lg transition-all duration-200 flex items-center',
                        type === typeKey ? activeBgClass : `bg-gray-50 ${bgHoverClass}`
                      )}
                    >
                      <Icon size={28} className={`${iconColorClass} mr-3 flex-shrink-0`} />
                      <div className="text-left">
                        <div className="font-bold text-sm font-sora">{label}</div>
                        <div className="text-xs text-gray-500 line-clamp-1">{typeDesc}</div>
                      </div>
                    </button>
                  );
                })}
            </div>

            {/* Mockup Preview Section - Desktop only */}
            <div className="mt-10 mb-0 hidden sm:flex justify-center">
              <div className="w-[640px]">
                {(() => {
                  // Map topic types to mockup types
                  const mockupMap: Record<string, string> = {
                    todo: 'Tasks',
                    notes: 'Notes',
                    ai_chat: 'AI Team Chat',
                    list: 'Lists',
                    moodboard: 'Moodboard',
                    bookmarks: 'Bookmarks',
                    table: 'Table',
                  };

                  const mockupType = mockupMap[type];
                  const mockup = MOCKUPS.find(m => m.type === mockupType);

                  // The mockups already have their own container with h-[380px] class and internal scroll
                  return mockup ? mockup.mockup : null;
                })()}
              </div>
            </div>

            {error && (
              <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">
                {error}
              </div>
            )}
          </form>

          {/* Bottom padding/spacer */}
          <div className="py-4"></div>
        </div>
      </div>
    </>
  );
}